import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const progressIndicatorVariants = cva(
  "h-full w-full flex-1 rounded-full transition-all duration-300",
  {
    variants: {
      variant: {
        default: "bg-primary",
        success: "bg-emerald-500",
        destructive: "bg-destructive",
        muted: "bg-muted-foreground/40",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface ProgressProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof progressIndicatorVariants> {
  /** 0–100. Ignored when `indeterminate` is set. */
  value?: number | null
  /** Show a looping animation when the total isn't known yet. */
  indeterminate?: boolean
}

/**
 * Lightweight progress bar. Self-contained (no Radix dependency) so it can be
 * used anywhere — file uploads, sync progress, build progress. Pass `value`
 * (0–100) for a determinate bar, or `indeterminate` when the total is unknown.
 */
const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, variant, indeterminate = false, ...props }, ref) => {
    const clamped = Math.max(0, Math.min(100, value ?? 0))
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : clamped}
        className={cn(
          "relative h-2 w-full overflow-hidden rounded-full bg-muted",
          className
        )}
        {...props}
      >
        {indeterminate ? (
          <div
            className={cn(
              progressIndicatorVariants({ variant }),
              "absolute inset-y-0 left-0 w-1/3 animate-progress-indeterminate"
            )}
          />
        ) : (
          <div
            className={cn(progressIndicatorVariants({ variant }))}
            style={{ width: `${clamped}%` }}
          />
        )}
      </div>
    )
  }
)
Progress.displayName = "Progress"

export { Progress, progressIndicatorVariants }
